import React, {useState, useRef} from "react";
import {useNavigate} from "react-router-dom";
import {
  submitFeedback,
  FeedbackData,
  uploadScreenshot,
} from "../services/api/feedbackService";
import {X, Image as ImageIcon} from "lucide-react";

const Feedback: React.FC = () => {
  const navigate = useNavigate();
  const [feedbackType, setFeedbackType] = useState<
    "general" | "anonymous" | "bug"
  >("general");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [siteLocation, setSiteLocation] = useState("");
  const [expectedBehaviour, setExpectedBehaviour] = useState("");
  const [observedBehaviour, setObservedBehaviour] = useState("");
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      // Validate required fields
      if (!subject.trim()) {
        alert("Subject is required");
        return;
      }

      if (!message.trim()) {
        alert("Message is required");
        return;
      }

      if (feedbackType === "general") {
        if (!name.trim()) {
          alert("Name is required for general feedback");
          return;
        }
        if (!email.trim()) {
          alert("Email is required for general feedback");
          return;
        }
      }

      let screenshotUrl: string | undefined;

      // Upload screenshot if provided (screenshot is optional)
      if (screenshot) {
        console.log("Uploading screenshot...");
        screenshotUrl = await uploadScreenshot(screenshot);
        console.log("Screenshot uploaded successfully:", screenshotUrl);
      } else {
        console.log("No screenshot provided - proceeding without screenshot");
      }

      // Build feedback data object, filtering out undefined values
      const feedbackData: FeedbackData = {
        type: feedbackType,
        subject,
        message,
        ...(feedbackType === "general" && {name, email}),
        ...(feedbackType === "bug" && {
          ...(siteLocation && {siteLocation}),
          ...(expectedBehaviour && {expectedBehaviour}),
          ...(observedBehaviour && {observedBehaviour}),
          ...(screenshotUrl && {screenshotUrl}),
        }),
      };

      console.log("Submitting feedback data:", feedbackData);
      await submitFeedback(feedbackData);

      setSubmitStatus("success");
      setTimeout(() => {
        navigate("/");
      }, 3000);
    } catch (error) {
      console.error("Feedback submission error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTypeChange = (type: "general" | "anonymous" | "bug") => {
    setFeedbackType(type);
    if (type === "anonymous") {
      setName("");
      setEmail("");
    }
    // Clear bug-specific fields when switching away from bug type
    if (type !== "bug") {
      setSiteLocation("");
      setExpectedBehaviour("");
      setObservedBehaviour("");
      setScreenshot(null);
      setScreenshotPreview(null);
    }
  };

  const handleScreenshotSelect = (file: File) => {
    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      alert("Only image files (JPEG, PNG) are allowed");
      return;
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert("Screenshot size must be less than 5MB");
      return;
    }

    setScreenshot(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setScreenshotPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleScreenshotRemove = () => {
    setScreenshot(null);
    setScreenshotPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleScreenshotSelect(file);
    }
  };

  return (
    <div className="feedback-page">
      <div className="container">
        <div className="feedback-container">
          <h1>Feedback</h1>
          <p className="feedback-intro">
            We value your feedback! Help us improve the League of Fabled
            Pursuits by sharing your thoughts, suggestions, or reporting any
            issues you've encountered.
          </p>

          <div className="feedback-type-selector">
            <h3>Feedback Type</h3>
            <div className="feedback-type-options">
              <label className="feedback-type-option">
                <input
                  type="radio"
                  name="feedbackType"
                  value="general"
                  checked={feedbackType === "general"}
                  onChange={() => handleTypeChange("general")}
                />
                <span className="radio-custom"></span>
                <div className="option-content">
                  <h4>General Feedback</h4>
                  <p>Share your name and email for follow-up communication</p>
                </div>
              </label>

              <label className="feedback-type-option">
                <input
                  type="radio"
                  name="feedbackType"
                  value="anonymous"
                  checked={feedbackType === "anonymous"}
                  onChange={() => handleTypeChange("anonymous")}
                />
                <span className="radio-custom"></span>
                <div className="option-content">
                  <h4>Anonymous Feedback</h4>
                  <p>Submit feedback without providing personal information</p>
                </div>
              </label>

              <label className="feedback-type-option">
                <input
                  type="radio"
                  name="feedbackType"
                  value="bug"
                  checked={feedbackType === "bug"}
                  onChange={() => handleTypeChange("bug")}
                />
                <span className="radio-custom"></span>
                <div className="option-content">
                  <h4>Bug Report</h4>
                  <p>
                    Report technical issues with additional details and
                    screenshots
                  </p>
                </div>
              </label>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="feedback-form" noValidate>
            {feedbackType === "general" && (
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Name *</label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="subject">Subject *</label>
              <input
                type="text"
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={
                  feedbackType === "bug"
                    ? "Brief description of the bug"
                    : "Brief description of your feedback"
                }
              />
            </div>

            {feedbackType === "bug" && (
              <>
                <div className="form-group">
                  <label htmlFor="siteLocation">Site Location (Optional)</label>
                  <input
                    type="text"
                    id="siteLocation"
                    value={siteLocation}
                    onChange={(e) => setSiteLocation(e.target.value)}
                    placeholder="e.g., /characters page, /sessions, etc."
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="expectedBehaviour">
                      Expected Behaviour (Optional)
                    </label>
                    <textarea
                      id="expectedBehaviour"
                      value={expectedBehaviour}
                      onChange={(e) => setExpectedBehaviour(e.target.value)}
                      placeholder="What should have happened?"
                      rows={3}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="observedBehaviour">
                      Observed Behaviour (Optional)
                    </label>
                    <textarea
                      id="observedBehaviour"
                      value={observedBehaviour}
                      onChange={(e) => setObservedBehaviour(e.target.value)}
                      placeholder="What actually happened?"
                      rows={3}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="screenshot">Screenshot (Optional)</label>
                  <div className="screenshot-upload-area">
                    {screenshotPreview ? (
                      <div className="screenshot-preview">
                        <img src={screenshotPreview} alt="Screenshot preview" />
                        <button
                          type="button"
                          className="screenshot-remove"
                          onClick={handleScreenshotRemove}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <div
                        className="screenshot-upload-placeholder"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <ImageIcon size={24} />
                        <p>Click to upload screenshot</p>
                        <span className="screenshot-note">
                          JPEG, PNG - max 5MB
                        </span>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".jpg,.jpeg,.png"
                      onChange={handleFileInputChange}
                      style={{display: "none"}}
                      formNoValidate
                    />
                  </div>
                </div>
              </>
            )}

            <div className="form-group">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={
                  feedbackType === "bug"
                    ? "Please provide detailed information about the bug, including steps to reproduce, error messages, and any other relevant details..."
                    : "Please provide detailed feedback, suggestions, or describe any issues you've encountered..."
                }
                rows={6}
              />
            </div>

            <div className="feedback-actions">
              <button
                type="button"
                onClick={() => navigate("/")}
                className="btn btn-secondary"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit Feedback"}
              </button>
            </div>
          </form>

          {submitStatus === "success" && (
            <div className="feedback-success">
              <div className="success-icon">✓</div>
              <h3>Thank You!</h3>
              <p>
                Your feedback has been submitted successfully. We appreciate you
                taking the time to help us improve!
              </p>
              <p>You'll be redirected to the home page shortly...</p>
            </div>
          )}

          {submitStatus === "error" && (
            <div className="feedback-error">
              <h3>Submission Error</h3>
              <p>
                Sorry, there was an error submitting your feedback. Please try
                again or contact us directly.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Feedback;
